<template>
  <div
    class="horizontal-layout"
    :class="[layoutClasses]"
    :data-col="isNavMenuHidden ? '1-column' : null"
    style="height:inherit"
  >

    <!-- NAVBAR -->
    <b-navbar
      :style="{
        backgroundColor: navbarType === 'static' && scrolledTo && skin === 'light' ? 'white' : null,
        boxShadow: navbarType === 'static' && scrolledTo ? 'rgba(0, 0, 0, 0.05) 0px 4px 20px 0px' : null,
      }"
      :toggleable="false"
      class="header-navbar navbar-shadow align-items-center navbar-brand-center navbar-fixed main-navbar"
      :class="{'fixed-top': $store.getters['app/currentBreakPoint'] !== 'xl'}"
    >
      <slot
        name="navbar"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
      >
        <app-navbar-horizontal-layout-brand />
        <app-navbar-horizontal-layout :toggle-vertical-menu-active="toggleVerticalMenuActive" />
      </slot>
    </b-navbar>
    <!--/ NAVBAR -->

    <div class="horizontal-menu-wrapper">
      <vue-perfect-scrollbar
        v-if="!isNavMenuHidden"
        ref="horizontalMenuPerfectScrollbar"
        :settings="perfectScrollbarSettings"
        class="main-menu-content scroll-area p-0 m-0 overflow-hidden position-fixed w-100"
        :class="{'h-100': updateGroupIsOpen}"
        tagname="ul"
        @wheel.native="scrollX"
      >
        <div
          class="header-navbar navbar-expand-sm navbar navbar-horizontal w-100 navbar-light navbar-shadow menu-border d-none d-xl-block position-relative"
          :class="[navbarMenuTypeClass]"
        >
          <horizontal-nav-menu @update-group-open="onUpdateGroupOpen"></horizontal-nav-menu>
        </div>
        <div
          v-if="showScrollIconStatus()"
          class="position-absolute chevron-right-icon-wrapper cursor-pointer"
          title="Для прокрутки меню, пожалуйста, используйте колесо мыши или тачпад, либо нажатие левой кнопки мыши"
          @click="scrollX"
        >
          <feather-icon
            icon="ChevronRightIcon"
            size="19"
          ></feather-icon>
        </div>
      </vue-perfect-scrollbar>

      <!-- Vertical Nav Menu -->
      <vertical-nav-menu
        :is-vertical-menu-active="isVerticalMenuActive"
        :toggle-vertical-menu-active="toggleVerticalMenuActive"
        class="d-block d-xl-none"
      >
        <template #header="slotProps">
          <slot
            name="vertical-menu-header"
            v-bind="slotProps"
          />
        </template>
      </vertical-nav-menu>
    <!-- /Vertical Nav Menu -->
    </div>

    <!-- Vertical Nav Menu Overlay -->
    <div
      class="sidenav-overlay"
      :class="overlayClasses"
      @click="isVerticalMenuActive = false"
    />
    <!-- /Vertical Nav Menu Overlay -->

    <!-- CONTENT -->
    <!-- CONTENT TYPE: Left -->
    <transition
      :name="routerTransition"
      mode="out-in"
    >
      <component
        :is="layoutContentRenderer"
        :key="layoutContentRenderer === 'layout-content-renderer-left' ? $route.meta.navActiveLink || $route.name : null"
      >
        <template
          v-for="(index, name) in $scopedSlots"
          v-slot:[name]="data"
        >
          <slot
            :name="name"
            v-bind="data"
          />
        </template>
      </component>
    </transition>
    <!--/ Content -->
    <!--/ CONTENT -->

    <!-- Footer -->
    <footer
      class="footer footer-light"
      :class="[footerTypeClass]"
    >
      <slot name="footer">
        <app-footer />
      </slot>
    </footer>

    <slot name="customizer" />
  </div>
</template>

<script>
import AppBreadcrumb from '@/core/layouts/components/AppBreadcrumb.vue'
import AppNavbarHorizontalLayout from '@/core/layouts/components/app-navbar/AppNavbarHorizontalLayout.vue'
import AppNavbarHorizontalLayoutBrand from '@/core/layouts/components/app-navbar/AppNavbarHorizontalLayoutBrand.vue'
import AppFooter from '@/core/layouts/components/AppFooter.vue'
import useAppConfig from '@/core/app-config/useAppConfig'
import { BNavbar } from 'bootstrap-vue'
import { useScrollListener } from '@/core/comp-functions/misc/event-listeners'
import { onUnmounted, ref } from 'vue'

// Content Renderer
import LayoutContentRendererDefault from '@/core/layouts/components/layout-content-renderer/LayoutContentRendererDefault.vue'
import LayoutContentRendererLeft from '@/core/layouts/components/layout-content-renderer/LayoutContentRendererLeft.vue'
import LayoutContentRendererLeftDetached from '@/core/layouts/components/layout-content-renderer/LayoutContentRendererLeftDetached.vue'
import useLayoutHorizontal from '@/core/layouts/layout-horizontal/useLayoutHorizontal'
import HorizontalNavMenu from '@/core/layouts/layout-horizontal/components/horizontal-nav-menu/HorizontalNavMenu.vue'

// Vertical Menu

import VerticalNavMenu from '@/core/layouts/layout-vertical/components/vertical-nav-menu/VerticalNavMenu.vue'
import useVerticalLayout from '@/core/layouts/layout-vertical/useVerticalLayout'
import mixinLayoutHorizontal from '@/core/layouts/layout-horizontal/mixinLayoutHorizontal'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'


export default {
  components: {
    AppBreadcrumb,
    AppNavbarHorizontalLayout,
    AppNavbarHorizontalLayoutBrand,
    AppFooter,
    HorizontalNavMenu,

    BNavbar,

    // Content Renderer
    LayoutContentRendererDefault,
    LayoutContentRendererLeft,
    LayoutContentRendererLeftDetached,

    // Vertical Menu
    VerticalNavMenu,

    VuePerfectScrollbar
  },
  mixins: [mixinLayoutHorizontal],
  computed: {
    layoutContentRenderer() {
      const rendererType = this.$route.meta.contentRenderer
      if (rendererType === 'sidebar-left') return 'layout-content-renderer-left'
      if (rendererType === 'sidebar-left-detached') return 'layout-content-renderer-left-detached'
      return 'layout-content-renderer-default'
    },
  },
  setup() {
    const {
      skin,
      navbarType,
      footerType,
      routerTransition,
      isNavMenuHidden,
    } = useAppConfig()

    // Vertical Menu
    const {
      isVerticalMenuActive, toggleVerticalMenuActive, overlayClasses, resizeHandler,
    } = useVerticalLayout(navbarType, footerType)

    // Resize handler
    resizeHandler()
    window.addEventListener('resize', resizeHandler)
    onUnmounted(() => {
      window.removeEventListener('resize', resizeHandler)
    })

    const {
      navbarMenuTypeClass,
      layoutClasses,
      footerTypeClass,
    } = useLayoutHorizontal(navbarType, footerType, isVerticalMenuActive)

    // Scroll Listener
    const { scrolledTo } = useScrollListener()

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
      suppressScrollY: true,
      swipeEasing: true,
      handlers: ['click-rail', 'drag-thumb', 'keyboard', 'wheel', 'touch']
    }

    const updateGroupIsOpen = ref(false)
    const onUpdateGroupOpen = (event) => {
      updateGroupIsOpen.value = event
    }

    const horizontalMenuPerfectScrollbar = ref(null)
    let counter = 1
    const scrollX = event => {
      const items = horizontalMenuPerfectScrollbar.value.$el.getElementsByClassName('nav-item')
      const elementsLength = items.length
      let chevronRightIconWrapper = horizontalMenuPerfectScrollbar.value.$el.getElementsByClassName(
        'chevron-right-icon-wrapper'
      )
      chevronRightIconWrapper = chevronRightIconWrapper && chevronRightIconWrapper.length
        ? chevronRightIconWrapper[0] : null
      if (chevronRightIconWrapper) {
        const scrollToLeft = event.wheelDelta > 0
        if (chevronRightIconWrapper.style.display === '' && !scrollToLeft) {
          const deltaY = event.deltaY ? event.deltaY : 100
          if (
            chevronRightIconWrapper.getBoundingClientRect().right
            >= items[elementsLength - 1].getBoundingClientRect().right - deltaY
          ) {
            chevronRightIconWrapper.style.display = 'none'
            chevronRightIconWrapper.style.right = -(deltaY * counter) + 'px'
            horizontalMenuPerfectScrollbar.value.$el.scrollLeft += parseFloat(deltaY)
          } else {
            chevronRightIconWrapper.style.right = -(deltaY * counter) + 'px'
            horizontalMenuPerfectScrollbar.value.$el.scrollLeft += parseFloat(deltaY)
            if (!scrollToLeft) {
              counter += 1
            }
          }
        } else if (scrollToLeft) {
          chevronRightIconWrapper.style.display = ''
          counter = counter > 1 ? counter - 1 : 0
          chevronRightIconWrapper.style.right = counter ? (event.deltaY * counter) + 38 + 'px' : (event.deltaY * counter) + 'px'
          horizontalMenuPerfectScrollbar.value.$el.scrollLeft += parseFloat(event.deltaY)
          counter = counter === 0 ? 1 : counter
        }
      }
    }

    const showScrollIconStatus = () => {
      if (horizontalMenuPerfectScrollbar.value) {
        const items = horizontalMenuPerfectScrollbar.value.$el.getElementsByClassName('nav-item')
        const elementsLength = items.length
        const mainMenuNavigation = horizontalMenuPerfectScrollbar.value.$el.getElementsByClassName(
          'main-menu-navigation'
        )[0]
        return mainMenuNavigation && items[elementsLength - 1]
          ? mainMenuNavigation.getBoundingClientRect().right < items[elementsLength - 1].getBoundingClientRect().right
          : false
      }
      return false
    }
    return {
      // skin
      skin,

      // Layout
      layoutClasses,

      // Navbar
      navbarType,
      navbarMenuTypeClass,

      // Menu Hidden
      isNavMenuHidden,

      // Router Transition
      routerTransition,

      // Footer
      footerTypeClass,

      // Scroll Listeners
      scrolledTo,

      // Vertical Menu
      isVerticalMenuActive,
      toggleVerticalMenuActive,
      overlayClasses,

      perfectScrollbarSettings,
      onUpdateGroupOpen,
      updateGroupIsOpen,
      scrollX,
      horizontalMenuPerfectScrollbar,
      showScrollIconStatus
    }
  },
}
</script>

<style lang='scss'>
@import '@/core/scss/base/themes/bordered-layout.scss';
@import '@/assets/scss/style';

.horizontal-menu-wrapper {
  .navbar-horizontal {
    margin: 2rem 0 2rem 0 !important;
    border-radius: 0 !important;
    .nav-item {
      &:first-child {
        margin-left: 2rem;
      }
      &:last-child {
        padding-right: 1rem;
      }
    }
  }
  .chevron-right-icon-wrapper {
    top: 28px;
    display: flex;
    justify-content: center;
    z-index: 999;
    width: 60px;
    right: 0;
    padding: 25px 0;
    background-color: $body-bg;
    box-shadow: $box-shadow;
  }
  .ps__rail-x {
    display: none !important;
  }
}

.dark-layout {
  .chevron-right-icon-wrapper {
    background-color: $theme-dark-card-bg;
    box-shadow: $theme-dark-box-shadow;
  }
}
</style>
